import * as angular from 'angular';
import { Observable, Subscription, fromEvent } from 'rxjs';

const POINTER_SIZE = 5;

class FeatureNuxtController implements angular.IController {
  static $inject = ['$element', '$scope', '$document', '$window'];

  private parentElem;
  private interval?;

  //bindings
  private position: string;
  private parentPosition: string;
  private offsetTop: string;
  private offsetBottom: string;
  private offsetLeft: string;
  private offsetRight: string;
  private appendToBody: string;
  private onClose: Function;

  // observables
  private resizeObservable$: Observable<Event>;

  // subscribes
  private resizeSubscribe$: Subscription;

  constructor(
    private $element: angular.IAugmentedJQuery,
    private $scope: angular.IScope,
    private $document: angular.IDocumentService,
    private $window: angular.IWindowService
  ) {}

  $onInit() {
    if (this.appendToBody === 'true') {
      this.resizeObservable$ = fromEvent(this.$window, 'resize');
      this.resizeSubscribe$ = this.resizeObservable$.subscribe((evt) => {
        if (evt) {
          this.setNuxtAppendPosition(this.$element);
        }
      });
    }
  }

  $postLink() {
    if (this.parentPosition) {
      this.setParentPosition(this.$element);
    }

    if (this.appendToBody === 'true') {
      // in the event the parent is not visible
      // and we need to append the nuxt to the body
      // this prevents a visible "jump" between when
      // the nuxt is initially loaded and it being
      // properly positioned on the page
      this.$element.css('opacity', 0);

      this.interval = setInterval(() => {
        if (this.$element.parent().is(':visible')) {
          this.parentElem = this.$element.parent();
          this.$scope.$evalAsync(() => {
            this.setNuxtAppendPosition(this.$element);
            this.$element.css('opacity', 1);
          });
          clearInterval(this.interval);
        }
      }, 100);
    } else {
      // in the event that the  parent is not visible
      // on initial page load, keep checking for it
      // until it's visible to ensure that we can
      // properly get the width and height of the so
      // that we can properly position the nuxt
      this.$element.hide();

      this.interval = setInterval(() => {
        if (this.$element.parent().is(':visible')) {
          this.$scope.$evalAsync(() => {
            this.setNuxtInlinePosition(this.$element);
            this.$element.show();
          });
          clearInterval(this.interval);
        }
      }, 100);
    }
  }

  $onDestroy() {
    this.interval && clearInterval(this.interval);
    this.resizeSubscribe$ && this.resizeSubscribe$.unsubscribe();
    this.$element.remove();
  }

  setNuxtAppendPosition(nuxtElement) {
    const nuxtPosition = this.getNuxtAppendPosition(nuxtElement);
    this.$document.find('body').append(nuxtElement.css(nuxtPosition));
  }

  getParentLocation() {
    const parent = this.parentElem[0].getBoundingClientRect();
    const scrollLeft = this.$window.pageXOffset || this.$document[0].documentElement.scrollLeft;
    const scrollTop = this.$window.pageYOffset || this.$document[0].documentElement.scrollTop;

    return {
      top: parent.top + scrollTop,
      left: parent.left + scrollLeft,
    };
  }

  getNuxtAppendPosition(nuxtElement) {
    const parentLocation = this.getParentLocation();
    const parentHeight = this.parentElem.outerHeight();
    const parentWidth = this.parentElem.outerWidth();
    const nuxtWidth = nuxtElement.children('.feature-nuxt').outerWidth();
    let defaultPosition;

    switch (this.position) {
      case 'left':
        defaultPosition = parentLocation.left + nuxtWidth + parentWidth + POINTER_SIZE - parentLocation.left;
        return {
          right: this.offsetRight ? this.offsetRight : defaultPosition,
          top: this.offsetTop ? this.offsetTop : parentLocation.top,
          position: 'fixed',
        };
        break;
      case 'right':
        defaultPosition = parentLocation.left + parentWidth + POINTER_SIZE;
        return {
          left: this.offsetLeft ? this.offsetLeft : defaultPosition,
          top: this.offsetTop ? this.offsetTop : parentLocation.top,
          position: 'fixed',
        };
        break;
      case 'bottom':
        defaultPosition = parentLocation.left - nuxtWidth / 2 + parentWidth / 2;
        return {
          left: this.offsetLeft ? this.offsetLeft : defaultPosition,
          top: this.offsetTop ? this.offsetTop : parentLocation.top + parentHeight,
          position: 'fixed',
        };
        break;
      default:
        defaultPosition = parentLocation.left + nuxtWidth + parentWidth + POINTER_SIZE - parentLocation.left;
        return {
          right: this.offsetRight ? this.offsetRight : defaultPosition,
          top: this.offsetTop ? this.offsetTop : parentLocation.top,
          position: 'fixed',
        };
    }
  }

  setParentPosition(nuxtElement) {
    const parent = nuxtElement.parent();
    parent.css('position', this.parentPosition);
  }

  setNuxtInlinePosition(nuxtElement) {
    const parentWidth = nuxtElement.parent().outerWidth();
    const parentHeight = nuxtElement.parent().outerHeight();
    const nuxtInner = nuxtElement.children('.feature-nuxt');

    switch (this.position) {
      case 'left':
        nuxtInner.css({
          right: this.offsetRight ? this.offsetRight : parentWidth + POINTER_SIZE,
          top: this.offsetTop ? this.offsetTop : 0,
          bottom: this.offsetBottom ? this.offsetBottom : 0,
        });
        break;
      case 'right':
        nuxtInner.css({
          left: this.offsetLeft ? this.offsetLeft : parentWidth + POINTER_SIZE,
          top: this.offsetTop ? this.offsetTop : 0,
          bottom: this.offsetBottom ? this.offsetBottom : 0,
        });
        break;
      case 'bottom':
        nuxtInner.css({
          left: this.offsetLeft ? this.offsetLeft : 'auto',
          right: this.offsetRight ? this.offsetRight : 'auto',
          top: this.offsetTop ? this.offsetTop : parentHeight + POINTER_SIZE,
        });
        break;
      default:
        nuxtInner.css({
          right: this.offsetRight ? this.offsetRight : parentWidth + POINTER_SIZE,
          top: this.offsetTop ? this.offsetTop : 0,
          bottom: this.offsetBottom ? this.offsetBottom : 0,
        });
    }
  }
}

export const FeatureNuxtComponent: angular.IComponentOptions = {
  bindings: {
    message: '<',
    iconClass: '@?',
    position: '@',
    onClose: '&?',
    parentPosition: '@?',
    showNew: '@?',
    class: '@?',
    offsetTop: '@?',
    offsetBottom: '@?',
    offsetLeft: '@?',
    offsetRight: '@?',
    appendToBody: '@?',
  },
  controller: FeatureNuxtController,
  controllerAs: 'ctrl',
  template: `
	<div class="feature-nuxt {{ ctrl.position }} {{ctrl.class}}">
		<div class="feature-nuxt__badge" ng-if="ctrl.showNew">New</div>
		<div class="feature-nuxt__icon" ng-if="ctrl.iconClass"><i class="{{ ctrl.iconClass }}"></i></div>
    <div class="feature-nuxt__message" ng-bind-html="ctrl.message"></div>
		<div class="feature-nuxt__close" ng-if="ctrl.onClose" ng-click="ctrl.onClose()">
			<i class="icon-close-small"></i>
		</div>
	</div>
	`,
};
